import { useContext } from 'react';
import type { FunctionComponent } from 'react';
import { useMediaQuery } from '@mantine/hooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router';
import {
  ActionIcon,
  AppShell,
  Box,
  Grid,
  Group,
  Overlay,
  ScrollArea,
  useComputedColorScheme,
} from '@mantine/core';
import NavList from 'components/NavList/NavList';
import LocationsSelector from 'components/LocationsSelector/LocationsSelector';
import CubohLeftNavSelector from 'components/LocationsSelector/CubohLocationsSelector/CubohLeftNavSelector';

import { LOCATION_HOME_PATH, LOCATION_PATH, REPORTS_PATH } from 'constants/path';
import { FEATURE_FLAGS } from 'constants/chownow';
import {
  CHANNEL_MANAGER,
  CHANNEL_INSIGHTS,
  DIRECT_SETTINGS,
  HOME,
  HQ_DETAILS,
  INTEGRATIONS,
  LOCATION_HOME,
  MARKETING,
  MENU,
  ORDERS,
  OVERVIEW,
  PUSH_NOTIFICATIONS,
  REPORTS,
  REPORTS_LOCATION_BASED,
  USERS,
} from 'scopes/nav';
import { useAuth } from 'hooks/useAuth';
import { useScopes } from 'hooks/useScopes';
import { HeadquarterContext } from 'context/HeadquarterContext';
import {
  menuLinks,
  reportingLinks,
  cubohReportingLinks,
  locationReportingLinks,
  orderLinks,
  locationBasedOrderLinks,
  locationSettingsLinks,
  marketingLinks,
  integrationsLinks,
} from 'Router';

import {
  getIntegrationsNavLinks,
  getReportsNavLinks,
  type NavLink,
  type RouteLink,
} from 'components/NavLeft/navLinks';
import { useIsChannelInsights } from 'hooks/useIsChannelInsights';
import { useTranslation } from 'i18n';

import { breakpoints } from 'styles/theme.variables';

import { ChowNowMarkLogoInk, ChowNowMarkLogoWhite } from '@chownow/pantry/logos';
import { CloseIconSm } from '@chownow/pantry/icons';

const NS = 'navLeft';

type NavLeftProps = {
  sideMenuOpened?: boolean;
  toggleSideMenu?: () => void;
};

function getLocationSettingsNavLinks(locationPath: string, links: RouteLink[]): NavLink[] {
  return (
    links
      // hide catering settings from left nav but keep route
      .filter(link => link.path !== 'catering')
      .map(link => ({
        path: link.path ? `${locationPath}/settings/${link.path}` : `${locationPath}/settings`,
        label: link.handle?.name,
        key: link.handle?.key,
      }))
  );
}

export const NavLeft: FunctionComponent<NavLeftProps> = ({ sideMenuOpened, toggleSideMenu }) => {
  const flags = useFlags();
  const isChannelManagerEnabled = flags?.[FEATURE_FLAGS.channelManager];
  const isChannelInsights = useIsChannelInsights();
  const { t } = useTranslation(NS);
  const showMobileNav = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const { pathname } = useLocation();
  const { isUserCubohOnly } = useAuth();
  const { locationIDToUseForNavigation } = useScopes() as { locationIDToUseForNavigation: string };
  const { headquarter } = useContext(HeadquarterContext);
  const isHqLevelPage = !pathname.includes(LOCATION_PATH);

  // In Mantine, Aside comes in from the right and Nav the left
  // so we use the Navbar element for desktop and the Aside for mobile
  const MantineNavElement = showMobileNav ? AppShell.Aside : AppShell.Navbar;

  // The nav background flips with the color scheme, so the mark must too — the old
  // separator SVG did this via CSS vars; pantry ships fixed-color variants instead.
  const colorScheme = useComputedColorScheme('light');
  const ChowNowMark = colorScheme === 'dark' ? ChowNowMarkLogoWhite : ChowNowMarkLogoInk;

  const locationPath = `${LOCATION_PATH}/${locationIDToUseForNavigation}`;

  const dynamicDirectLinks = getLocationSettingsNavLinks(locationPath, locationSettingsLinks);

  const hqNavLinks = headquarter
    ? [
        {
          label: t('NavLeft.home'),
          path: '/',
          key: HOME,
        },
        {
          label: t('NavLeft.orders'),
          key: ORDERS,
          links: orderLinks.map(link => ({
            path: `/company/${headquarter.id}/orders/${link.path ?? ''}`,
            label: link.handle?.name,
            key: link.handle?.key,
          })),
        },
        {
          label: t('NavLeft.hqDetails'),
          path: `/company/${headquarter.id}`,
          key: HQ_DETAILS,
        },
        {
          label: t('NavLeft.users'),
          path: `/company/${headquarter.id}/users`,
          key: USERS,
        },
        {
          label: t('NavLeft.pushNotifications'),
          path: `/company/${headquarter.id}/notifications`,
          key: PUSH_NOTIFICATIONS,
        },
      ]
    : [
        {
          label: t('NavLeft.home'),
          path: '/',
          key: HOME,
        },
        // CN-41387 reports on HQ level are HUB only
        {
          label: t('NavLeft.reports'),
          key: REPORTS,
          links: [...reportingLinks, ...cubohReportingLinks].map(link => ({
            path: `/${REPORTS_PATH}/${link.path}`,
            label: link.handle?.name,
            key: link.handle?.key,
          })),
        },
      ];

  const channelManagerLink = {
    label: t('NavLeft.channelManager'),
    key: CHANNEL_MANAGER,
    path: `${locationPath}/channel-manager`,
  };
  const overviewLink = {
    label: t('NavLeft.overview'),
    key: OVERVIEW,
    path: `${locationPath}/overview`,
  };
  // Channel Insights lives under Reports instead of Overview
  let channelManagerOrOverviewLinks = isChannelInsights ? [] : [overviewLink];
  // Channel Manager takes the top-level Overview slot, independent of Channel Insights
  if (isChannelManagerEnabled) {
    channelManagerOrOverviewLinks = [channelManagerLink];
  }

  const locationNavLinks = [
    {
      label: t('NavLeft.home'),
      key: LOCATION_HOME,
      path: `${locationPath}/${LOCATION_HOME_PATH}`,
    },
    ...channelManagerOrOverviewLinks,
    {
      label: t('NavLeft.orders'),
      key: ORDERS,
      links: locationBasedOrderLinks.map(link => ({
        path: `${locationPath}/orders/${link.path ?? ''}`,
        label: link.handle?.name,
        key: link.handle?.key,
      })),
    },
    {
      label: t('NavLeft.reports'),
      key: REPORTS_LOCATION_BASED,
      links: getReportsNavLinks(locationPath, REPORTS_PATH, locationReportingLinks, {
        isChannelManagerEnabled,
        isChannelInsights,
        channelInsightsKey: CHANNEL_INSIGHTS,
        channelInsightsLabel: t('NavLeft.channelInsights'),
      }),
    },
    {
      label: t('NavLeft.menus'),
      key: MENU,
      links: menuLinks.map(link => ({
        path: `${locationPath}/${link.path}`,
        label: link.handle?.name,
        key: link.handle?.key,
      })),
    },
    {
      label: t('NavLeft.integrations'),
      key: INTEGRATIONS,
      links: getIntegrationsNavLinks(locationPath, integrationsLinks, {
        isChannelManagerEnabled,
      }),
    },
    {
      label: t('NavLeft.marketing'),
      key: MARKETING,
      links: marketingLinks
        .filter(link => !link.path.includes('/'))
        .map(link => ({
          path: `${locationPath}/${link.path}`,
          label: link.handle?.name,
          key: link.handle?.key,
        })),
    },
    {
      label: t('NavLeft.settings'),
      key: DIRECT_SETTINGS,
      links: dynamicDirectLinks,
    },
  ];

  return (
    <>
      {sideMenuOpened && <Overlay hiddenFrom="md" onClick={toggleSideMenu} />}
      <MantineNavElement
        style={{ borderColor: 'var(--border-secondary)' }}
        zIndex={
          sideMenuOpened ? 'calc(var(--mantine-z-index-overlay) + 1)' : 'var(--mantine-z-index-app)'
        }
      >
        <Grid gutter={0}>
          <Grid.Col
            span={12}
            order={{ base: 2, md: 1 }}
            p={0}
            mb={{ sm: 'var(--mantine-spacing-sm)', md: '0px' }}
          >
            {isUserCubohOnly ? (
              <CubohLeftNavSelector toggleNav={toggleSideMenu} />
            ) : (
              <LocationsSelector toggleNav={toggleSideMenu} />
            )}
          </Grid.Col>
          <Grid.Col span={12} order={{ base: 1, md: 2 }} hiddenFrom="md">
            <Group justify="space-between" px="md" pt="md">
              <ChowNowMark height={32} width={29} />
              <ActionIcon onClick={toggleSideMenu} hiddenFrom="md" size="md">
                <CloseIconSm />
              </ActionIcon>
            </Group>
          </Grid.Col>
        </Grid>
        <ScrollArea
          p={{ base: '0px var(--mantine-spacing-sm)', lg: '0px var(--mantine-spacing-xs)' }}
          type="never"
        >
          {!showMobileNav && (
            <Box h="32px" my="var(--mantine-spacing-md)" ml="var(--mantine-spacing-xs2)">
              <ChowNowMark height={32} width={29} />
            </Box>
          )}
          <NavList navLinks={isHqLevelPage ? hqNavLinks : locationNavLinks} />
        </ScrollArea>
      </MantineNavElement>
    </>
  );
};
